import type { ResolvedIntlConfig } from 'react-intl'
import { buildLocaleMessages } from './buildLocaleMessages'
import { isSupportedLocale, SUPPORTED_LOCALES, type SupportedLocale } from './types'

const messageModules = import.meta.glob('./messages/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, Record<string, unknown>>

function messagePathForLocale(locale: SupportedLocale): string {
  return `./messages/${locale}.json`
}

/** Nested message JSON bundles keyed by locale (pre-flatten). */
export const MESSAGE_BUNDLES = Object.fromEntries(
  SUPPORTED_LOCALES.map((locale) => {
    const path = messagePathForLocale(locale)
    const bundle = messageModules[path]
    if (!bundle) {
      throw new Error(`Missing message bundle for locale ${locale} at ${path}`)
    }
    return [locale, bundle]
  })
) as Record<SupportedLocale, Record<string, unknown>>

// Fail fast if a JSON file exists under messages/ for an unknown tag (typo / stale file).
for (const path of Object.keys(messageModules)) {
  const match = /^\.\/messages\/(.+)\.json$/.exec(path)
  const tag = match?.[1]
  if (tag && !isSupportedLocale(tag)) {
    throw new Error(`Message bundle ${path} is not a SupportedLocale`)
  }
}

export type MessageBundle = (typeof MESSAGE_BUNDLES)[SupportedLocale]

/** Flattened react-intl message maps keyed by locale. */
export const MESSAGES_BY_LOCALE = Object.fromEntries(
  SUPPORTED_LOCALES.map((locale) => [locale, buildLocaleMessages(MESSAGE_BUNDLES[locale])])
) as Record<SupportedLocale, ResolvedIntlConfig['messages']>
